/**
 * Blinkit-theme checkout (``/checkout``, reached from "Buy now"). All state and actions — live re-pricing for the
 * chosen address / coupon / coins, the pin-less-address location gate, wallet, COD / Razorpay and placing the order —
 * come from useCheckoutController (the classic checkout's own logic). Shares its sections with BkCartView.
 */
import { useNavigate } from "react-router-dom";
import { MapPin, NotebookPen, Package } from "lucide-react";
import { fmtInr } from "../../../lib/priceBreakdown";
import { coinCapSubtotal } from "../../../lib/coinRedemption";
import { api } from "../../../lib/api";
import AddressPickerModal from "../../../components/AddressPickerModal";
import { BkCard, BkErrorState, BkPageHeader } from "../../../components/blinkit/BkUI";
import CouponPickerModal from "../_partials/CouponPickerModal";
import { CoinsRedeemBlock } from "../_partials/CartPickers";
import { CheckoutAddressDrawer } from "../_partials/CheckoutPartials";
import { useCheckoutController } from "../controllers/useCheckoutController";
import DeliveryOptions from "../../../components/lse/DeliveryOptions";
import { deliveryBlocking } from "../../../lib/lse";
import { cartShopLabel } from "../../../lib/multiShop";
import { ShopBreakdown, ShopDivider } from "../../../components/MultiShop";
import {
    BkAddressSection, BkCouponSection, BkNotice, BkPaymentOverlay, BkPaymentSection, BkPlaceOrderBar, BkPriceDetails, PAY_ICONS,
} from "./BkCheckoutParts";
import { BkCartSkeleton } from "./BkCartView";

export default function BkCheckoutView() {
    const nav = useNavigate();
    const c = useCheckoutController({ onPlaced: (orderId) => nav(`/orders/${orderId}`, { replace: true, state: { justPlaced: true } }) });
    const {
        razorpayMock, addrList, selectedAddrId, setSelectedAddrId, showPicker, setShowPicker, showAddNew, setShowAddNew,
        payment, setPayment, useWallet, setUseWallet, walletBalance, notes, setNotes, busy, payPhase, lastError, setLastError,
        liveCartError, retryLiveCart,
        liveCart, couponInput, setCouponInput, couponDraft, setCouponDraft, showCouponPicker, setShowCouponPicker,
        availCoupons, couponPickerBusy, coinsBalance, coinsConfig, coinsToRedeem, setCoinsToRedeem, loadAddrs, liveTotals,
        grandTotal, coinMax, coinMin, handleAddNew, selectedAddr, showSetLocation, setShowSetLocation, locationNeeded,
        openCouponPicker, applyCouponFromPicker, placeOrder,
    } = c;

    // A coupon saved on the cart earlier is priced by the server's bill and applied by checkout itself
    // (effective_cart_coupon), so the bill below is always what the order is charged.
    const removeCoupon = async () => {
        // Clear the saved coupon on the server FIRST, then reload — otherwise the reload could still see it.
        try { await api.post("/coupons/remove"); } catch { /* soft-fail — the bill below stays the source of truth */ }
        setCouponInput("");
        if (couponDraft) setCouponDraft(""); else retryLiveCart();
    };

    const items = liveCart?.items || [];
    const walletApplied = useWallet ? Math.min(walletBalance, grandTotal) : 0;
    const toPay = Math.max(0, Math.round((grandTotal - walletApplied) * 100) / 100);
    const outOfZone = !!liveTotals.delivery_out_of_zone;
    const couponApplied = liveTotals.coupon_code && Number(liveTotals.coupon_discount) > 0 ? liveTotals.coupon_code : null;
    const methods = [
        // ``codAllowed`` is false only for a multi-shop cart in a tenant that hasn't allowed cash for several shops.
        c.codAllowed !== false && { id: "COD", label: "Cash on delivery", sub: "Pay with cash when your order arrives", icon: PAY_ICONS.COD, testid: "pay-cod" },
        { id: "RAZORPAY", label: "Pay online", sub: razorpayMock ? "UPI, cards, netbanking (test mode)" : "UPI, cards, netbanking — secured by Razorpay", icon: PAY_ICONS.RAZORPAY, testid: "pay-online" },
    ].filter(Boolean);
    // Location Service Engine tenants only: a delivery option still to pick / an area rule the cart breaks.
    const lseBlock = deliveryBlocking(c.lse);
    const disabled = busy || !liveCart || !selectedAddr || outOfZone || locationNeeded || !!lseBlock;
    const label = outOfZone ? "Can't deliver to this address"
        : lseBlock ? lseBlock
        : locationNeeded ? "Set delivery location"
            : !selectedAddr ? "Add an address to continue"
                : useWallet && walletBalance >= grandTotal ? "Pay with wallet"
                    : payment === "RAZORPAY" ? "Pay & place order" : "Place order";

    // One pay bar, rendered in exactly one place: inside the summary column once the order is on screen (static from lg,
    // ``fixed`` below it — so its DOM position changes nothing on phones and tablets), otherwise after the loading /
    // error state, where it has always been.
    const loaded = !!liveCart || !selectedAddrId;
    const payBar = (
        <BkPlaceOrderBar amount={liveCart ? toPay : null} label={label} disabled={disabled} busy={busy} onClick={placeOrder} testid="place-order-btn"
                         busyLabel={payPhase === "paying" ? "Waiting for payment…" : payPhase === "confirming" ? "Confirming payment…" : "Placing order…"} />
    );

    return (
        <div className="pb-48 lg:pb-16 min-w-0" data-testid="bk-checkout-page">
            <BkPageHeader title="Checkout" subtitle={liveCart ? `${cartShopLabel(liveCart)} · ${items.reduce((s, i) => s + i.quantity, 0)} items` : null} back="/cart" />

            {!liveCart && selectedAddrId && liveCartError ? (
                <BkErrorState title="Couldn't load your order" body="Check your connection and try again — your cart is saved." onRetry={retryLiveCart} testid="bk-checkout-error" />
            ) : !liveCart && selectedAddrId ? <BkCartSkeleton /> : (
                <div className="px-4 pt-4 lg:grid lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-5 lg:items-start">
                    <div className="space-y-3 min-w-0">
                        <BkAddressSection
                            addr={selectedAddr}
                            onChange={() => setShowPicker(true)}
                            onAdd={() => setShowAddNew(true)}
                            warning={<>
                                {locationNeeded && (
                                    <BkNotice testid="checkout-location-needed-banner" title="Where exactly should we deliver?"
                                              body={`This address has no map location and pincode ${selectedAddr?.pincode || ""} isn't one we recognise. Set it on the map (or fix the pincode) so the store and rider know where to go.`}
                                              action={<button type="button" onClick={() => setShowSetLocation(true)} data-testid="checkout-set-location" className="h-8 px-3 rounded-lg bg-amber-600 text-white text-[12px] font-bold inline-flex items-center gap-1"><MapPin size={12} /> Set location on map</button>} />
                                )}
                                {outOfZone && (
                                    <BkNotice tone="error" testid="checkout-out-of-zone-banner" title="This store doesn't deliver to this address"
                                              body={`It's ${liveTotals.delivery_distance_km} km away; the store delivers within ${liveTotals.delivery_zone_radius_km} km.`}
                                              action={<button type="button" onClick={() => setShowPicker(true)} data-testid="checkout-out-of-zone-change" className="h-8 px-3 rounded-lg bg-destructive text-white text-[12px] font-bold">Choose another address</button>} />
                                )}
                            </>}
                        />

                        {c.lse && <div className="mt-3"><DeliveryOptions lse={c.lse} value={c.delivery} onChange={c.chooseDelivery} compact /></div>}

                        {items.length > 0 && (
                            <BkCard title="Your order" icon={Package} testid="bk-checkout-items">
                                <ul className="divide-y divide-border">
                                    {items.map((it, idx) => [
                                        <ShopDivider key={`shop-${it.product_id}-${it.variant_id || ""}`} as="li" cart={liveCart} items={items} index={idx} />,
                                        <li key={`${it.product_id}-${it.variant_id || ""}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                                            <span className="w-11 h-11 shrink-0 rounded-lg bg-muted/60 overflow-hidden flex items-center justify-center">
                                                {it.image_url ? <img src={it.image_url} alt="" className="w-full h-full object-contain" /> : <Package size={16} className="text-muted-foreground/60" />}
                                            </span>
                                            <span className="min-w-0 flex-1 text-[13px] font-semibold leading-snug line-clamp-2">{it.name}</span>
                                            <span className="shrink-0 text-[12px] text-muted-foreground tabular-nums">× {it.quantity}</span>
                                            <span className="shrink-0 w-16 text-right text-[13px] font-bold tabular-nums">{fmtInr(it.line_total)}</span>
                                        </li>,
                                    ])}
                                </ul>
                            </BkCard>
                        )}

                        {/* Multi-shop checkout only (renders nothing otherwise): what each shop's order comes to. */}
                        <ShopBreakdown cart={liveCart} className="mt-3" />

                        <BkCouponSection
                            appliedCode={couponApplied}
                            saved={Number(liveTotals.coupon_discount || 0)}
                            onOpenPicker={openCouponPicker}
                            onRemove={removeCoupon}
                            code={couponInput} setCode={setCouponInput} onApply={() => setCouponDraft(couponInput.trim())}
                            invalid={couponDraft && !liveTotals.coupon_discount ? "This code didn't apply — check the spelling, expiry or minimum order value." : null}
                        >
                            <CoinsRedeemBlock coinsBalance={coinsBalance} coinsConfig={coinsConfig} coinsToRedeem={coinsToRedeem} setCoinsToRedeem={setCoinsToRedeem}
                                              coinMax={coinMax} coinMin={coinMin} coinsDisc={liveTotals.coins_discount || 0} subtotalForCap={coinCapSubtotal(liveTotals)} />
                        </BkCouponSection>

                        <BkCard title="Delivery instructions" icon={NotebookPen}>
                            <textarea data-testid="notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={300}
                                      placeholder="E.g. ring the bell, leave at the door" aria-label="Delivery instructions"
                                      className="w-full rounded-xl border border-border bg-muted/40 px-3 py-2.5 text-[13.5px] outline-none focus:border-primary focus:bg-card resize-none" />
                        </BkCard>
                    </div>

                    <div className="space-y-3 mt-3 lg:mt-0 min-w-0 lg:sticky lg:top-20">
                        <BkPaymentSection methods={methods} value={payment} onChange={setPayment} walletBalance={walletBalance}
                                          useWallet={useWallet} setUseWallet={setUseWallet} total={grandTotal} />
                        {liveCart && <BkPriceDetails totals={liveTotals} walletApplied={walletApplied} testid="checkout-summary" />}
                        {lastError && (
                            <BkNotice tone="error" testid="bk-order-error" title="Your order wasn't placed" body={`${lastError}. Nothing was charged — you can try again.`}
                                      onDismiss={() => setLastError(null)}
                                      action={<button type="button" onClick={placeOrder} disabled={disabled} className="h-8 px-3 rounded-lg bg-destructive text-white text-[12px] font-bold disabled:opacity-50">Try again</button>} />
                        )}
                        {payBar}
                    </div>
                </div>
            )}

            {!loaded && payBar}
            <BkPaymentOverlay phase={payPhase} />

            {showPicker && (
                <CheckoutAddressDrawer addrList={addrList} selectedAddrId={selectedAddrId}
                                       onPick={(id) => { setSelectedAddrId(id); setShowPicker(false); }}
                                       onAddNew={() => { setShowPicker(false); setShowAddNew(true); }}
                                       onClose={() => setShowPicker(false)} />
            )}
            {showAddNew && <AddressPickerModal onClose={() => setShowAddNew(false)} onSaved={handleAddNew} />}
            {showSetLocation && selectedAddr && (
                <AddressPickerModal initial={selectedAddr} onClose={() => setShowSetLocation(false)}
                                    onSaved={async () => { await loadAddrs(false); setShowSetLocation(false); }} />
            )}
            {showCouponPicker && (
                <CouponPickerModal busy={false} available={availCoupons} loading={couponPickerBusy} onApply={applyCouponFromPicker} onClose={() => setShowCouponPicker(false)} />
            )}
        </div>
    );
}
